// What «Verificación» says about an element, apart from the work the team did: a correction declared is not a correction
// checked. Pure function, tested in verification.test.ts.
import type { FollowState } from './api';

export type VerificationKind =
  /** Nothing has checked it and nobody said the fix is in place. */
  | 'sin-comprobar'
  /** The fix is declared, or the last check could not decide: it still has to be checked. */
  | 'pendiente'
  /** A check found it gone. */
  | 'verificado'
  /** A check found it again. */
  | 'persiste'
  /** A person decided the detection is not a problem: nothing to verify. */
  | 'descartada';

export interface Verification {
  kind: VerificationKind;
  /** Where the result comes from, when there is one. */
  source: 'automatica' | 'manual' | null;
  /** When the check was made. */
  at: string | null;
  /** The automatic check cannot settle it: a person has to look (a guided check). */
  needsManual: boolean;
}

/**
 * The verification of an element, from what the follow-up recorded. A check older than the moment the fix was declared
 * proves nothing about it (it looked at the page before the change), and one that could not decide is not a result.
 */
export function verificationOf(state: FollowState): Verification {
  const { check, work } = state;
  if (state.validation.status === 'descartada')
    return { kind: 'descartada', source: null, at: null, needsManual: false };
  if (work.status === 'archivada')
    return {
      kind: 'verificado',
      source: state.archive?.evidence?.source ?? check?.source ?? null,
      at: state.archive?.evidence?.at ?? check?.at ?? null,
      needsManual: false,
    };
  const applied = work.status === 'aplicada';
  const stale = Boolean(check && state.appliedAt && new Date(check.at) < new Date(state.appliedAt));
  const pending = (needsManual: boolean): Verification => ({
    kind: applied || needsManual ? 'pendiente' : 'sin-comprobar',
    source: null,
    at: null,
    needsManual: needsManual || state.manual.needsHuman,
  });
  if (!check || stale) return pending(false);
  if (check.result === 'no-detectado')
    return { kind: 'verificado', source: check.source, at: check.at, needsManual: false };
  if (check.result === 'sigue-detectandose')
    return { kind: 'persiste', source: check.source, at: check.at, needsManual: state.manual.needsHuman };
  // «No confirmado automáticamente» or «Requiere revisión manual»: still pending, and a person has to judge it.
  return { ...pending(true), source: check.source, at: check.at };
}
